import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { merchantAuthAPI, RequisiteType, MerchantAdvance } from '../lib/api';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../lib/analytics';
import { currentCabinetRole } from '../lib/domains';
import { clearMerchantSession, finishMerchantImpersonation, getMerchantAccessToken, isMerchantImpersonationActive, isMerchantImpersonationEntry, merchantImpersonationExpiresAt, storeMerchantUser } from '../lib/merchantSession';

export interface CommissionTier {
    min_amount: number;
    max_amount: number | null;
    commission_rate: number;
    max_amount_inclusive?: boolean;
}

export interface MerchantUser {
    hide_api_keys?: boolean;
    impersonation?: boolean;
    impersonated_by_admin_id?: string;
    impersonated_by_admin_username?: string;
    advance?: MerchantAdvance | null;
    id: string;
    username: string;
    email: string;
    company_name?: string;
    api_key: string;
    sandbox_api_key?: string;
    user_type: string;
    is_active: boolean;
    created_at: string;
    webhook_url?: string;
    webhook_secret?: string;
    sandbox_webhook_url?: string;
    sandbox_webhook_secret?: string;
    deposit_balance: number;
    settlement_balance?: number;
    settlement_rounding_adjustment_usdt?: number;
    available_balance_usdt?: number;
    withdraw_balance: number;
    deposit_commission_rate: number;
    withdraw_commission_rate: number;
    deposit_commission_tiers?: CommissionTier[];
    withdraw_commission_tiers?: CommissionTier[];
    deposit_commission_tiers_by_requisite_type?: Partial<Record<RequisiteType, CommissionTier[]>>;
    withdraw_commission_tiers_by_requisite_type?: Partial<Record<RequisiteType, CommissionTier[]>>;
    requisite_types?: RequisiteType[];
    deposit_min_amount?: number | null;
    deposit_max_amount?: number | null;
    withdraw_min_amount?: number | null;
    withdraw_max_amount?: number | null;
    rate_source?: string;
    rate_markup_percent?: number;
    exchange_rate?: number | null;
    appeal_lifetime_minutes: number;
    two_factor_enabled: boolean;
    api_ip_allowlist: string[];
    live_api_ip_allowlist?: string[];
    sandbox_api_ip_allowlist?: string[];
    integration_profile?: 'current' | 'typical_v2_2';
    fiat_currency?: 'RUB' | 'KGS' | 'KZT';
}

interface MerchantAuthContextType {
    user: MerchantUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    refreshUser: () => Promise<void>;
    logout: () => void;
}

const MerchantAuthContext = createContext<MerchantAuthContextType | undefined>(undefined);

export const useMerchantAuth = () => {
    const context = useContext(MerchantAuthContext);
    if (context === undefined) {
        throw new Error('useMerchantAuth должен использоваться внутри MerchantAuthProvider');
    }
    return context;
};

interface MerchantAuthProviderProps {
    children: ReactNode;
}

export const MerchantAuthProvider: React.FC<MerchantAuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<MerchantUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const isAuthenticated = !!user;

    const refreshUser = async () => {
        const token = getMerchantAccessToken();
        if (!token) return;
        try {
            const userData = await merchantAuthAPI.getCurrentUser();
            if (getMerchantAccessToken() !== token) return;
            setUser(userData as MerchantUser);
            storeMerchantUser(userData);
            if (!isMerchantImpersonationActive()) identifyAnalyticsUser('merchant', userData);
        } catch (error) {
            clearMerchantSession();
            setUser(null);
        }
    };

    useEffect(() => {
        // The handoff page initializes storage before reloading the dashboard.
        if (currentCabinetRole() !== 'merchant' || isMerchantImpersonationEntry()) {
            setIsLoading(false);
            return;
        }
        void refreshUser().finally(() => setIsLoading(false));
        // The provider is re-mounted after the handoff; no reactive dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isMerchantImpersonationEntry() || !isMerchantImpersonationActive()) return;
        const expiresAt = merchantImpersonationExpiresAt();
        const endSession = () => {
            setUser(null);
            finishMerchantImpersonation();
        };
        const checkExpiry = () => {
            if (Date.now() >= expiresAt) endSession();
        };
        const timer = window.setTimeout(endSession, Math.max(0, expiresAt - Date.now()));
        window.addEventListener('focus', checkExpiry);
        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('focus', checkExpiry);
        };
    }, []);

    const logout = () => {
        const impersonation = isMerchantImpersonationActive();
        merchantAuthAPI.logout();
        setUser(null);
        if (!impersonation) resetAnalyticsUser();
    };

    const value: MerchantAuthContextType = {
        user,
        isLoading,
        isAuthenticated,
        refreshUser,
        logout,
    };

    return (
        <MerchantAuthContext.Provider value={value}>
            {children}
        </MerchantAuthContext.Provider>
    );
};
